// pages/components/PricingSection.js
import Link from "next/link";
import { useEffect, useState } from "react";
import { auth, db } from "../../lib/firebase";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { useRouter } from "next/router";
import { getTrialStatus, startFreeTrial } from "../../lib/subscriptionUtils";
import { PRICING_PLANS } from "../../config/pricing";

// Convert centralized pricing to this component's format
const PLANS = PRICING_PLANS.map(plan => ({
  id: plan.id === 'Free' ? null : plan.id,
  name: plan.name,
  price: plan.price,
  hours: plan.hours,
  description: plan.description,
  cta: plan.cta
}));

export default function PricingSection() {
  const router = useRouter();
  const [user, setUser] = useState(null);
  // ✅ MOBILE SYNC: Enhanced state matching mobile subscription service
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const refreshUser = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const userDoc = await getDoc(doc(db, "users", currentUser.uid));
    if (userDoc.exists()) setUser(userDoc.data());
  };

  useEffect(() => {
    const fetchUser = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      const userData = userDoc.data();
      setUser(userData);

      // ✅ MOBILE SYNC: Load trial status like mobile
      const trialData = await getTrialStatus(currentUser.uid, userData);
      setTrialStatus(trialData);

      if (userData?.planId && userData.planId !== "free") {
        router.replace("/quester/dashboard");
      }
    };
    fetchUser();
  }, []);

  const handleStartTrial = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return alert("Please log in first");

    setLoading(true);

    // ✅ MOBILE SYNC: Use shared utility function
    const result = await startFreeTrial(currentUser.uid);
    
    if (result.success) {
      alert(`${result.title}\n\n${result.message}`);
      router.push("/quester/dashboard");
    } else {
      alert(`${result.title}\n\n${result.message}`);
    }
    
    setLoading(false);
  };

  const handleSubscribe = async (planId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return alert("Please log in");

    // Subscriptions stay on Stripe for now (hybrid: NMI for wallet top-ups,
    // Stripe for recurring sub MRR + Connect payouts). /api/stripe/create-
    // checkout-session handles all three paths internally:
    //   • New paid plan      → returns { url } → redirect to Stripe Checkout
    //   • Upgrade (prorated) → returns { success, type: 'upgrade' }
    //   • Downgrade          → returns { success, type: 'downgrade_scheduled', effectiveDate }
    try {
      const res = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId, uid: currentUser.uid }),
      });
      const session = await res.json();
      if (!res.ok) {
        alert(session?.message || session?.error || 'Failed to start checkout');
        return;
      }

      if (session.changed) {
        // Plan change applied server-side, no redirect needed.
        await refreshUser();
        if (session.type === 'downgrade_scheduled') {
          const when = session.effectiveDate
            ? new Date(session.effectiveDate).toLocaleDateString()
            : 'next billing date';
          alert(`Downgrade scheduled. Your plan will change on ${when}. You won't be charged today.`);
        } else {
          alert('Plan updated. Your new limits are active now.');
        }
        return;
      }

      if (!session?.url) {
        alert('Checkout URL missing from response');
        return;
      }
      window.location.href = session.url;
    } catch (error) {
      console.error('Stripe subscribe failed', error);
      alert('Failed to start checkout. Please try again.');
    }
    return;
  };

  const scheduledDowngradeBanner = (() => {
    const sd = user?.scheduledDowngrade;
    if (!sd?.planId) return null;
    let when = 'your next billing cycle';
    const eff = sd.effectiveDate;
    const parsed = eff?.seconds
      ? new Date(eff.seconds * 1000)
      : eff
        ? new Date(eff)
        : null;
    if (parsed && !isNaN(parsed.getTime())) {
      when = parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    const targetLabel = PRICING_PLANS.find((p) => p.id === sd.planId)?.name || sd.planId;
    return (
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6 flex items-start gap-3">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mt-0.5 text-amber-700 flex-shrink-0">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <div className="text-sm">
          <p className="font-semibold text-amber-800">Downgrade Scheduled</p>
          <p className="text-amber-700 mt-1">
            Your plan will change to <strong>{targetLabel}</strong> on <strong>{when}</strong>. You keep your current plan features until then.
          </p>
        </div>
      </div>
    );
  })();

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Simple, Transparent Pricing</h2>
      {scheduledDowngradeBanner}
<div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-10 text-center">
        <h2 className="text-xl font-semibold text-green-700">🎁 Free Plan Available</h2>
        <p className="text-gray-700 mt-2">
          Get 300 free minutes every month — forever!
          <br />
          💳 Free plan includes both audio and video calls. Upgrade anytime for more monthly minutes.
          <br />
          Calls are still billed at each Maven&apos;s listed per-minute rate.
        </p>
      </div>
      <div className="grid gap-6 md:grid-cols-4">
        {PLANS.map((plan, idx) => (
          <div
            key={idx}
            className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 text-center hover:shadow-md transition"
          >
            <h3 className="text-xl font-semibold text-black mb-2">{plan.name}</h3>
            <p className="text-3xl font-bold text-violet-600 mb-1">{plan.price}</p>
            {plan.priceWithTax && (
              <p className="text-xs text-gray-500 mb-1">({plan.priceWithTax} with NC tax)</p>
            )}
            <p className="text-gray-500 mb-2">{plan.hours}</p>
            <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
            <button
              onClick={() => plan.id ? handleSubscribe(plan.id) : handleStartTrial()}
              disabled={loading || (plan.id === null && trialStatus && !trialStatus.eligible)}
              className={`text-white text-sm px-6 py-2 rounded w-full transition-colors ${
                loading || (plan.id === null && trialStatus && !trialStatus.eligible)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-violet-600 hover:bg-violet-700'
              }`}
            >
              {loading && plan.id === null ? 'Starting Trial...' : 
               plan.id === null && trialStatus && !trialStatus.eligible ? 
               'Trial Used' : plan.cta}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingCard({ title, price, hours, planId, onSelect, currentPlan }) {
  const handleClick = () => {
    if (planId) {
      onSelect(planId);
    } else {
      onSelect(); // for free trial
    }
  };

  const isFreeTrial = !planId;
  const alreadyUpgraded = currentPlan && currentPlan !== "free";

  return (
    <div className="border rounded-lg p-6 shadow text-center bg-white">
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-3xl font-bold text-indigo-600">{price}</p>
      <p className="text-sm text-gray-500 mb-4">{hours}</p>
      <p className="text-gray-600 mb-6">Subscribe to unlock this plan.</p>
      {isFreeTrial && alreadyUpgraded ? (
        <p className="text-sm text-gray-400 italic">Free trial unavailable after upgrade</p>
      ) : (
        <button
          onClick={handleClick}
          className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
        >
          Choose Plan
        </button>
      )}
    </div>
  );
}
